import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import { registerSite } from '../../../Services/Api/siteService'; 

import 'leaflet-draw';

// Fix the icon paths issue in React with Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Styled Components
const FormContainer = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamily};
`;


// Export FormGroup
export const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

// Export Label
export const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

// Export Input
export const Input = styled.input`
  width: 95%;
  padding: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const MapContainer = styled.div`
  height: 500px;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Button = styled.button`
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  cursor: pointer;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  transition: background-color 0.2s;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DrawButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  
  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.secondary};
  }
`;

const CancelButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.red};
  color: ${({ theme }) => theme.colors.text.inverse};
  
  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.error};
  }
`;

const SubmitButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.success};
  color: ${({ theme }) => theme.colors.text.inverse};
  
  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const InputButtonContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: flex-end;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const InputGroup = styled.div`
  flex: 1;
`;

const ButtonGroup = styled.div`
  flex-shrink: 0;
`;

// Accept onSuccessfulSubmit prop
function Siteregisterform({ onSuccessfulSubmit }) { 
    const [siteName, setSiteName] = useState('');
    const [coordinates, setCoordinates] = useState(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [isLoading, setIsLoading] = useState(false); // Add loading state
    const [submitStatus, setSubmitStatus] = useState(null); // Add state for success/error message
    
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const drawnItemsRef = useRef(null);
    const drawControlRef = useRef(null);
    
    // Initialize map
    useEffect(() => {
        if (mapInstanceRef.current) return; // Skip if already initialized
        
        // Create map instance
        const map = L.map(mapRef.current).setView([6.9271, 79.8612], 12);
        
        // Add tile layer
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }).addTo(map);
        
        // Create feature group for drawn items
        const drawnItems = new L.FeatureGroup();
        map.addLayer(drawnItems);
        
        // Initialize draw control
        const drawControl = new L.Control.Draw({
            draw: {
                polyline: false,
                rectangle: false,
                circle: false,
                circlemarker: false,
                marker: false,
                polygon: {
                    allowIntersection: false,
                    showArea: true,
                    shapeOptions: {
                        color: '#3b82f6'
                    }
                }
            },
            edit: {
                featureGroup: drawnItems,
                remove: true
            }
        });
        
        // Add control to map but hide its UI
        map.addControl(drawControl);
        const drawElement = document.querySelector('.leaflet-draw');
        if (drawElement) {
            drawElement.style.display = 'none';
        }
        
        // Handle created shapes
        map.on('draw:created', function(event) {
            const layer = event.layer;
            drawnItems.addLayer(layer);
            
            // Extract and format coordinates
            try {
                const latLngs = layer.getLatLngs()[0]; // Get the outer ring LatLng objects
                
                // Map to [lng, lat] number pairs
                const extractedCoords = latLngs.map(latlng => [
                    parseFloat(latlng.lng.toFixed(6)), // Convert lng string to number
                    parseFloat(latlng.lat.toFixed(6))  // Convert lat string to number
                ]);

                // Ensure the polygon is closed by repeating the first coordinate at the end
                if (extractedCoords.length > 0) {
                    const firstCoord = extractedCoords[0];
                    const lastCoord = extractedCoords[extractedCoords.length - 1];
                    // Check if the first and last coordinates are already the same
                    if (firstCoord[0] !== lastCoord[0] || firstCoord[1] !== lastCoord[1]) {
                        extractedCoords.push(firstCoord); // Add the first coordinate to the end
                    }
                }
                
                console.log("Formatted Coordinates:", extractedCoords); // Log the formatted coordinates
                setCoordinates(extractedCoords); // Set the state with the correctly formatted array

            } catch (error) {
                console.error("Error processing coordinates:", error);
                setCoordinates(null); // Reset coordinates on error
            }
            
            setIsDrawing(false);
        });
        
        // Listen for draw stop event
        map.on('draw:drawstop', function() {
            setIsDrawing(false);
            
            if (drawnItems.getLayers().length === 0) {
                setCoordinates(null);
            }
        });
        
        // Store references
        mapInstanceRef.current = map;
        drawnItemsRef.current = drawnItems;
        drawControlRef.current = drawControl;
        
        // Cleanup
        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, []);
    
    // Start drawing polygon
    const handleStartDrawing = () => {
        if (!mapInstanceRef.current || isDrawing) return;
        
        // Clear previous drawings and status
        drawnItemsRef.current.clearLayers();
        setCoordinates(null);
        setSubmitStatus(null); // Clear previous status messages
        
        // Get the draw polygon button and click it
        const drawButton = document.querySelector('.leaflet-draw-draw-polygon');
        if (drawButton) {
            setTimeout(() => {
                drawButton.click();
                setIsDrawing(true);
            }, 50);
        }
    };
    
    // Cancel drawing
    const handleCancelDrawing = () => {
        const cancelButton = document.querySelector('.leaflet-draw-actions a[title="Cancel drawing"]');
        if (cancelButton) {
            cancelButton.click();
        } else {
            // Alternative: dispatch Escape key event
            document.dispatchEvent(new KeyboardEvent('keydown', {
                key: 'Escape',
                code: 'Escape',
                keyCode: 27,
                which: 27,
                bubbles: true
            }));
        }
        
        setIsDrawing(false);
        setSubmitStatus(null); // Clear previous status messages
    };
    
    const handleSubmit = async (e) => { 
        e.preventDefault();
        setSubmitStatus(null); 
        
        if (!siteName || !coordinates || coordinates.length < 4) { // A valid polygon needs at least 4 points (3 unique + closing point)
            setSubmitStatus({ type: 'error', message: 'Please enter a site name and draw a valid boundary (at least 3 points).' });
            return;
        }

        // The coordinates state should now be [[lng, lat], [lng, lat], ..., [lng, lat]] (numbers)
        // Wrap it in another array to match the expected GeoJSON structure: [[[lng, lat], ...]]
        const formattedCoordinates = [coordinates]; 

        const siteData = {
            name: siteName,
            boundary: {
                type: "Polygon",
                coordinates: formattedCoordinates 
            }
        };
        
        console.log("Submitting Site Data:", JSON.stringify(siteData, null, 2)); // Log the final payload
        
        setIsLoading(true); 

        try {
            // Call the service function
            const result = await registerSite(siteData); 
            
            setIsLoading(false); 
            console.log('Success:', result);
            setSubmitStatus({ type: 'success', message: 'Site registered successfully!' });
            
            // Call the callback function if provided
            if (onSuccessfulSubmit) {
                onSuccessfulSubmit(); 
            }


        } catch (error) {
            setIsLoading(false); 
            console.error('Submission Error:', error);
            // Display the error message thrown by the service
            setSubmitStatus({ type: 'error', message: error.message || 'An unexpected error occurred.' });
        }
    };
    
    return (
         <FormContainer>
        <form onSubmit={handleSubmit}>
           <FormGroup>
                <Label htmlFor="siteName">Site Name:</Label>
                <InputButtonContainer>
                    <InputGroup>
                        <Input 
                            type="text"
                            id="siteName"
                            name="siteName"
                            value={siteName}
                            onChange={(e) => setSiteName(e.target.value)}
                            placeholder="Enter site name"
                            required
                        />
                    </InputGroup>
                    <ButtonGroup>
                        <DrawButton 
                            type="button" 
                            onClick={handleStartDrawing}
                            disabled={isDrawing || isLoading}
                        >
                            Mark Location
                        </DrawButton>
                        {isDrawing && (
                            <CancelButton 
                                type="button" 
                                onClick={handleCancelDrawing}
                                disabled={isLoading}
                                style={{ marginLeft: '8px' }}
                            >
                                Cancel Drawing
                            </CancelButton>
                        )}
                        <SubmitButton 
                            type="submit" 
                            disabled={!coordinates || isLoading}
                            style={{ marginLeft: '8px' }}
                        >
                            {isLoading ? 'Registering...' : 'Register Site'} 
                        </SubmitButton>
                    </ButtonGroup>
                </InputButtonContainer>
            </FormGroup>
            
            <FormGroup>
                <Label>Site Location Map:</Label>
                <MapContainer ref={mapRef} />
            </FormGroup>

            {/* Display Success/Error Messages */}
            {submitStatus && (
                <div style={{ 
                    color: submitStatus.type === 'success' ? 'green' : 'red', 
                    marginBottom: '1rem',
                    fontWeight: 'bold' 
                }}>
                    {submitStatus.message}
                </div>
            )}
        </form>
    </FormContainer>
);
}

export default Siteregisterform;
